import { supabase } from './supabase'

/**
 * Which vehicle a line on a supplier invoice belongs to.
 *
 * Winnie, 11 Sep, section 2: "The invoice reader extracts the cost figures but does not
 * reliably resolve which vehicle a line item belongs to... Extract VIN and rego, VIN
 * match first, Rego as fallback (rego can change - re-plated vehicles, personalised
 * plates), else return unmatched() which surfaces the line item for manual vehicle
 * selection - never silently drops or guesses a vehicle."
 *
 * The matching is done against the vehicles we actually hold rather than against a
 * pattern. A VIN is self-identifying and can be found by shape, but an Australian plate
 * is six-odd characters of letters and digits, which describes half the tokens on a
 * freight invoice - consignment numbers, job numbers, dates. Pulling "the rego" out of
 * free text by pattern would invent vehicles. So the fleet is loaded and the invoice is
 * searched for the identifiers we know, which can be wrong about nothing it has not
 * already been told.
 *
 * Nothing here decides anything. It returns candidates, says how each was found, and
 * says plainly when it found none; the form puts them in front of a person.
 */

/** The ISO VIN alphabet: I, O and Q are excluded so they cannot be read as 1 and 0. */
const VIN_SHAPE = /\b[A-HJ-NPR-Z0-9]{17}\b/g

/** Case and punctuation are noise in both a VIN and a plate. "1AB 2CD" is "1AB2CD". */
export const normalise = (v: unknown): string =>
  String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * A plate shorter than this is not looked for at all. Four characters collide with too
 * much of what else is printed on an invoice, and a wrong vehicle on a cost line is
 * worse than no vehicle: one is a question, the other is a quiet error in the P&L.
 */
const MIN_REGO_LENGTH = 5

export interface FleetVehicle {
  dealId: string
  vin: string | null
  rego: string | null
  label: string
}

export interface VehicleMatch {
  dealId: string
  label: string
  /** How this vehicle was found. Worth showing: it is what makes the match checkable. */
  matchedOn: 'vin' | 'rego'
  /** The identifier as it was found in the invoice text. */
  matchedValue: string
}

export interface InvoiceMatchResult {
  matches: VehicleMatch[]
  /** True when nothing could be resolved. The line item still exists; it needs a person. */
  unmatched: boolean
  /** One sentence for the screen, saying what happened and what to do about it. */
  note: string
  /** VIN-shaped strings in the invoice that match no vehicle we hold. */
  unknownVins: string[]
}

/** The "nothing found" answer, as its own function so every caller returns the same shape. */
export const unmatchedResult = (note: string, unknownVins: string[] = []): InvoiceMatchResult => ({
  matches: [],
  unmatched: true,
  note,
  unknownVins,
})

/**
 * Every alphanumeric run in the text, plus each adjacent pair joined.
 *
 * The pair join is what finds a plate printed as "1AB 2CD" or "ABC-123". Without it the
 * match depends on how the supplier's template happens to space its own field.
 */
const searchTokens = (text: string): Set<string> => {
  const runs = String(text || '').toUpperCase().match(/[A-Z0-9]+/g) || []
  const out = new Set<string>(runs)
  for (let i = 0; i + 1 < runs.length; i++) out.add(runs[i] + runs[i + 1])
  return out
}

/**
 * Resolve an invoice's text against a known fleet.
 *
 * Pure, so it can be tested without a database, and so the caller can hold the fleet for
 * a whole invoice rather than reloading it for every line.
 */
export function matchVehiclesInText(text: string, fleet: FleetVehicle[]): InvoiceMatchResult {
  const raw = String(text || '')
  if (!raw.trim()) {
    return unmatchedResult('There was no readable text on this invoice, so no vehicle could be matched. Choose the vehicle for each line.')
  }

  const byVin = new Map<string, FleetVehicle>()
  for (const v of fleet) {
    const n = normalise(v.vin)
    if (n) byVin.set(n, v)
  }

  const vinsInText = [...new Set((raw.toUpperCase().match(VIN_SHAPE) || []).map(normalise))]

  const matches: VehicleMatch[] = []
  const claimed = new Set<string>()
  const unknownVins: string[] = []

  // VIN first. A VIN is stamped on the car and does not change hands with the plate.
  for (const vin of vinsInText) {
    const hit = byVin.get(vin)
    if (hit) {
      if (!claimed.has(hit.dealId)) {
        claimed.add(hit.dealId)
        matches.push({ dealId: hit.dealId, label: hit.label, matchedOn: 'vin', matchedValue: vin })
      }
    } else {
      unknownVins.push(vin)
    }
  }

  // Rego as the fallback, and only for vehicles the VIN pass did not already find.
  const tokens = searchTokens(raw)
  for (const v of fleet) {
    if (claimed.has(v.dealId)) continue
    const rego = normalise(v.rego)
    if (rego.length < MIN_REGO_LENGTH) continue
    if (tokens.has(rego)) {
      claimed.add(v.dealId)
      matches.push({ dealId: v.dealId, label: v.label, matchedOn: 'rego', matchedValue: rego })
    }
  }

  if (!matches.length) {
    return unmatchedResult(
      unknownVins.length
        ? 'The VIN ' +
          unknownVins.join(' and ') +
          ' is on this invoice but is not a vehicle on the system. Choose the vehicle for each line, or check the VIN on the deal.'
        : 'No VIN or registration on this invoice matched a vehicle on the system. Choose the vehicle for each line.',
      unknownVins,
    )
  }

  const regoOnly = matches.filter((m) => m.matchedOn === 'rego')
  const plural = matches.length === 1 ? 'vehicle' : 'vehicles'
  let note =
    matches.length +
    ' ' +
    plural +
    ' matched on this invoice' +
    (matches.length > 1 ? ' - one line has been started for each.' : '.')

  if (regoOnly.length) {
    note +=
      ' ' +
      (regoOnly.length === 1 ? 'One was' : regoOnly.length + ' were') +
      ' matched on registration rather than VIN, so please check it is the right car - plates change.'
  }

  return { matches, unmatched: false, note, unknownVins }
}

/**
 * The database-backed form. Loads every vehicle that could be on an invoice and resolves
 * the text against it.
 *
 * Sold and settled cars are deliberately included: freight and reconditioning invoices
 * routinely arrive after the car has gone, and a late cost on a sold vehicle is a normal
 * thing that has to land somewhere.
 */
export async function matchVehicleFromInvoice(extractedText: string): Promise<InvoiceMatchResult> {
  let fleet: FleetVehicle[] = []

  try {
    const { data, error } = await supabase
      .from('deals')
      .select('id, vehicle_vin, autograb_vin, registration_number, vehicle_make, vehicle_model, vehicle_year, autograb_make, autograb_model, autograb_year')
      .is('deleted_at', null)

    if (error) throw error

    fleet = ((data || []) as any[]).map((d) => {
      const make = d.autograb_make || d.vehicle_make || ''
      const model = d.autograb_model || d.vehicle_model || ''
      const year = d.autograb_year || d.vehicle_year || ''
      const name = [year, make, model].filter(Boolean).join(' ').trim()
      const plate = d.registration_number ? ' (' + d.registration_number + ')' : ''
      return {
        dealId: d.id as string,
        vin: (d.vehicle_vin || d.autograb_vin || null) as string | null,
        rego: (d.registration_number || null) as string | null,
        label: (name || 'Vehicle') + plate,
      }
    })
  } catch (e) {
    console.error('Could not load vehicles to match the invoice against', e)
    return unmatchedResult('The vehicle list could not be read, so nothing was matched automatically. Choose the vehicle for each line.')
  }

  return matchVehiclesInText(extractedText, fleet)
}
